import {
  addMonths,
  evalAssignInput,
  firstDay,
  lastDay,
  occurrencesBetween,
  formatMonthLong,
  formatMonthShort,
  monthName,
  monthOf,
  rtaState,
  type PlanCategoryDto,
  type PlanGroupDto,
} from '@koverta/shared';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Keypad, minorToRaw, rawToMinor } from '../components/Keypad';
import { useToast } from '../components/toast';
import {
  Avatar,
  Btn,
  CatPill,
  Dialog,
  Icon,
  Kbd,
  MoneyInput,
  Pill,
  RtaLabel,
  Sheet,
  Spinner,
  TargetBar,
  isSnoozedNow,
  statusText,
  targetProgress,
  targetSentence,
} from '../components/ui';
import { del, errorMessage, get, patch, post } from '../lib/api';
import { useClickOutside, useIsMobile } from '../lib/hooks';
import { useHousehold, type Fmt } from '../lib/household';
import { keys, queryClient, useScheduled, useTransactions } from '../lib/queries';
import { FILTERS, passFilter, usePlanModel, type Filter, type PlanModel } from './plan-model';
import { TargetDialog } from './TargetDialog';

export function PlanPage() {
  const { today } = useHousehold();
  const params = useParams();
  const month = params.month && /^\d{4}-\d{2}$/.test(params.month) ? params.month : monthOf(today);
  const model = usePlanModel(month);
  const mobile = useIsMobile();
  if (model.isLoading) return <Spinner />;
  if (model.error || !model.plan)
    return <div className="banner warn">Couldn't load the plan: {errorMessage(model.error)}</div>;
  return mobile ? <PlanMobile model={model} month={month} /> : <PlanDesktop model={model} month={month} />;
}

/** The category named in `?cat=`, read once when the page opens. */
function initialCategory(): string | null {
  return new URLSearchParams(window.location.search).get('cat');
}

function useMonthNav(month: string) {
  const navigate = useNavigate();
  return (delta: number) => navigate(`/plan/${addMonths(month, delta)}`);
}

const collapsedKey = 'koverta.collapsed';
function useCollapsed() {
  const [set, setSet] = useState<Set<string>>(
    () => new Set(JSON.parse(localStorage.getItem(collapsedKey) ?? '[]')),
  );
  const toggle = (id: string) =>
    setSet((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      localStorage.setItem(collapsedKey, JSON.stringify([...n]));
      return n;
    });
  return [set, toggle] as const;
}

// ───────────────────────── Desktop ─────────────────────────

function PlanDesktop({ model, month }: { model: PlanModel; month: string }) {
  const { fmt, today } = useHousehold();
  const { toast } = useToast();
  const go = useMonthNav(month);
  const navigate = useNavigate();
  const plan = model.plan!;
  const [filter, setFilter] = useState<Filter>('all');
  const [showHidden, setShowHidden] = useState(false);
  // ?cat=<id> (from Predictions' month-end forecast) opens that category's inspector.
  const [sel, setSel] = useState<string | null>(() => initialCategory());
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [pop, setPop] = useState<string | null>(null);
  const [collapsed, toggleGroup] = useCollapsed();
  const [editing, setEditing] = useState<{ id: string; text: string; original: number } | null>(null);
  const [dialog, setDialog] = useState<null | StructDialog | { kind: 'target'; id: string }>(null);
  const hideItem = useHideItem();
  const [dragId, setDragId] = useState<string | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const st = rtaState(model.rta);

  const visibleGroups = model.groups.filter((g) => !g.hidden);
  const inputs = () => [...(tableRef.current?.querySelectorAll<HTMLInputElement>('input.as-in') ?? [])];
  const focusFirstUnderfunded = () => {
    const target = model.cats.find((c) => !c.hidden && (c.state === 'warn' || c.state === 'neg'));
    const el = target && tableRef.current?.querySelector<HTMLInputElement>(`input[data-as="${target.id}"]`);
    if (el) el.focus();
    else inputs()[0]?.focus();
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const list = inputs();
      const next = list[list.indexOf(e.currentTarget) + (e.shiftKey ? -1 : 1)];
      if (next) next.focus();
      else e.currentTarget.blur();
    } else if (e.key === 'Escape') e.currentTarget.blur();
  };

  const onDrop = async (e: DragEvent, groupId: string, beforeId: string | null) => {
    e.preventDefault();
    const id = dragId;
    setDragId(null);
    if (!id || id === beforeId) return;
    const layout = model.groups.map((g) => ({
      groupId: g.id,
      categoryIds: g.categories.map((c) => c.id).filter((x) => x !== id),
    }));
    const target = layout.find((g) => g.groupId === groupId)!;
    const idx = beforeId ? target.categoryIds.indexOf(beforeId) : -1;
    target.categoryIds.splice(idx >= 0 ? idx : target.categoryIds.length, 0, id);
    try {
      await post('/api/categories/reorder', { layout });
    } catch (x) {
      toast(errorMessage(x));
    }
    await queryClient.invalidateQueries({ queryKey: keys.plan(month) });
  };

  const counts = useMemo(
    () =>
      Object.fromEntries(
        FILTERS.map(([f]) => [f, model.cats.filter((c) => !c.hidden && passFilter(c, f, month)).length]),
      ),
    [model.cats, month],
  );
  const selected = sel ? model.byId.get(sel) : undefined;
  const hiddenEntries = useMemo(() => hiddenEntriesOf(model.groups), [model.groups]);

  return (
    <div className={`plan ${selected || checked.size ? '' : 'no-sel'}`}>
      <div className="plan-main">
        <div className="plan-head">
          <div className="month">
            <button className="ib" onClick={() => go(-1)} aria-label="Previous month">
              <Icon name="chevL" />
            </button>
            <b>{formatMonthShort(month)}</b>
            <button className="ib" onClick={() => go(1)} aria-label="Next month">
              <Icon name="chevR" />
            </button>
            {month !== monthOf(today) ? (
              <button className="today-b" onClick={() => navigate('/plan')}>
                Today
              </button>
            ) : null}
          </div>
          <div className="rta-wrap">
            <div className={`rta ${st}`} data-testid="rta">
              <div>
                <div className="rta-l">
                  <RtaLabel rta={model.rta} />
                </div>
                <div className="rta-n">
                  {fmt.num(model.rta)}
                  <span> {fmt.base}</span>
                </div>
              </div>
              {model.rta !== 0 ? (
                <AssignMenu model={model} fmt={fmt} onTypeAmounts={focusFirstUnderfunded} />
              ) : (
                <span style={{ paddingRight: 8 }} aria-label="All money assigned">
                  <Icon name="check" size={24} stroke={2} />
                </span>
              )}
            </div>
            <FxLine fx={plan.fxThisMonth} fmt={fmt} />
          </div>
          <div className="head-stats">
            <div className="stat">
              <small>Age of money</small>
              <b>
                {plan.ageOfMoney ?? '—'} <span>days</span>
              </b>
            </div>
            <div className="stat">
              <small>Underfunded</small>
              <b className="warn-t" data-testid="underfunded">
                {fmt.num(model.underfunded)}
              </b>
            </div>
          </div>
        </div>

        <div className="toolbar">
          {FILTERS.map(([k, l]) => (
            <button
              key={k}
              className={`chip ${filter === k && !showHidden ? 'on' : ''}`}
              onClick={() => {
                setFilter(k);
                setShowHidden(false);
              }}
              aria-pressed={filter === k && !showHidden}
            >
              {l} <span className="cnt">{counts[k]}</span>
            </button>
          ))}
          {hiddenEntries.length || showHidden ? (
            <button
              className={`chip ${showHidden ? 'on' : ''}`}
              onClick={() => setShowHidden((v) => !v)}
              aria-pressed={showHidden}
            >
              Hidden <span className="cnt">{hiddenEntries.length}</span>
            </button>
          ) : null}
          <span className="sp" />
          <Btn variant="ghost" icon="undo" onClick={() => model.undo()}>
            Undo
          </Btn>
          <Btn icon="plus" onClick={() => setDialog({ kind: 'group' })}>
            Category group
          </Btn>
        </div>

        {showHidden ? (
          <div className="ct" aria-label="Hidden categories">
            <HiddenList entries={hiddenEntries} />
          </div>
        ) : (
          <div className="ct" ref={tableRef} aria-label={`Plan for ${formatMonthLong(month)}`}>
            <div className="ct-row th">
              <span>
                <input
                  type="checkbox"
                  className="cb"
                  tabIndex={-1}
                  aria-label="Select all"
                  checked={checked.size > 0 && checked.size === model.cats.filter((c) => !c.hidden).length}
                  onChange={(e) =>
                    setChecked(
                      e.target.checked
                        ? new Set(model.cats.filter((c) => !c.hidden).map((c) => c.id))
                        : new Set(),
                    )
                  }
                />
              </span>
              <span>Category</span>
              <span>Assigned</span>
              <span>Activity</span>
              <span>Available</span>
            </div>
            {plan.uncategorized.available !== 0 || plan.uncategorized.activity !== 0 ? (
              <div className="ct-row cat uncat">
                <span />
                <div className="cn">
                  <div className="nm">Uncategorized transactions</div>
                  <div className="tg">
                    <span className="st neg">Give these a category in the register or the Inbox</span>
                  </div>
                </div>
                <div className="r" />
                <div className="r act-c">{fmt.num(plan.uncategorized.activity)}</div>
                <div className="pill-c">
                  <Pill
                    state={plan.uncategorized.available < 0 ? 'neg' : 'zero'}
                    value={fmt.num(plan.uncategorized.available)}
                  />
                </div>
              </div>
            ) : null}
            {visibleGroups.map((g) => {
              const cats = g.categories.filter((c) => !c.hidden && passFilter(c, filter, month));
              if (!cats.length && filter !== 'all') return null;
              const open = !collapsed.has(g.id);
              const sum = (k: 'assigned' | 'activity' | 'available') =>
                g.categories.filter((c) => !c.hidden).reduce((s, c) => s + c[k], 0);
              return (
                <div key={g.id}>
                  <div
                    className="ct-row grp"
                    onClick={() => toggleGroup(g.id)}
                    onDragOver={(e) => dragId && e.preventDefault()}
                    onDrop={(e) => onDrop(e, g.id, g.categories[0]?.id ?? null)}
                  >
                    <span className={`caret ${open ? '' : 'shut'}`}>
                      <Icon name="chevD" size={15} />
                    </span>
                    <span className="grp-name">
                      {g.name}
                      <button
                        className="grp-add"
                        tabIndex={-1}
                        aria-label={`Add a category to ${g.name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDialog({ kind: 'category', groupId: g.id });
                        }}
                      >
                        <Icon name="plus" size={12} stroke={2} /> Category
                      </button>
                      <RowMenu
                        label={`${g.name} actions`}
                        items={[
                          {
                            label: 'Rename group',
                            onClick: () => setDialog({ kind: 'rename-group', id: g.id }),
                          },
                          { label: 'Hide group', onClick: () => hideItem('group', g.id, g.name) },
                          {
                            label: 'Delete group…',
                            danger: true,
                            onClick: () => setDialog({ kind: 'delete-group', id: g.id }),
                          },
                        ]}
                      />
                    </span>
                    <span className="r">{fmt.num(sum('assigned'))}</span>
                    <span className="r">{fmt.num(sum('activity'))}</span>
                    <span className="r">{fmt.num(sum('available'))}</span>
                  </div>
                  {open && !g.categories.some((c) => !c.hidden) ? (
                    <div className="ct-row empty-grp">
                      <span />
                      <span>
                        Nothing in {g.name} yet.{' '}
                        <button
                          className="link"
                          onClick={() => setDialog({ kind: 'category', groupId: g.id })}
                        >
                          Add a category
                        </button>
                      </span>
                    </div>
                  ) : null}
                  {open
                    ? cats.map((c) => (
                        <CategoryRowDesk
                          key={c.id}
                          c={c}
                          month={month}
                          fmt={fmt}
                          selected={sel === c.id}
                          checked={checked.has(c.id)}
                          onCheck={(v) =>
                            setChecked((s) => {
                              const n = new Set(s);
                              if (v) n.add(c.id);
                              else n.delete(c.id);
                              return n;
                            })
                          }
                          onSelect={() => setSel(sel === c.id ? null : c.id)}
                          onAction={(a) =>
                            a === 'hide'
                              ? hideItem('category', c.id, c.name)
                              : setDialog({
                                  kind: a === 'rename' ? 'rename-category' : 'delete-category',
                                  id: c.id,
                                })
                          }
                          editing={editing?.id === c.id ? editing : null}
                          onFocus={(input) => {
                            const text = fmt.num(c.assigned, 2);
                            setSel(c.id);
                            setEditing({ id: c.id, text, original: c.assigned });
                            // Select synchronously so fast typing right after Tab replaces the value.
                            input.select();
                            requestAnimationFrame(() => input.value === text && input.select());
                          }}
                          onType={(text) => {
                            setEditing((ed) => (ed ? { ...ed, text } : ed));
                            const v = evalAssignInput(text, editing?.original ?? c.assigned, fmt.locale);
                            if (v !== null) model.setDraft(c.id, v);
                          }}
                          onBlur={() => setEditing(null)}
                          onKey={onKey}
                          pop={pop === c.id}
                          onPill={() => {
                            setSel(c.id);
                            setPop(pop === c.id ? null : c.id);
                          }}
                          closePop={() => setPop(null)}
                          model={model}
                          dragging={dragId === c.id}
                          onDragStart={() => setDragId(c.id)}
                          onDrop={(e) => onDrop(e, g.id, c.id)}
                          dragActive={!!dragId}
                        />
                      ))
                    : null}
                </div>
              );
            })}
            {filter !== 'all' && !model.cats.some((c) => !c.hidden && passFilter(c, filter, month)) ? (
              <div className="empty" style={{ padding: 40 }}>
                <p>Nothing matches this filter.</p>
              </div>
            ) : null}
          </div>
        )}
        <div className="kbhint">
          <span>
            <Kbd>Tab</Kbd> / <Kbd>↵</Kbd> next cell
          </span>
          <span>
            <Kbd>⇧ Tab</Kbd> previous
          </span>
          <span>Type +500, −1.000 or *2 to adjust</span>
          <span>Click a pill to move money</span>
          <span>All amounts in {fmt.base} (base currency)</span>
        </div>
      </div>
      {checked.size > 1 ? (
        <BulkInspector ids={[...checked]} model={model} fmt={fmt} onClear={() => setChecked(new Set())} />
      ) : selected ? (
        <Inspector
          c={selected}
          group={model.groups.find((g) => g.id === selected.groupId)!}
          month={month}
          model={model}
          onClose={() => setSel(null)}
          onEditTarget={() => setDialog({ kind: 'target', id: selected.id })}
          onDelete={() => setDialog({ kind: 'delete-category', id: selected.id })}
        />
      ) : (
        <MonthSummary model={model} month={month} />
      )}
      <StructureDialogs dialog={dialog} model={model} onClose={() => setDialog(null)} />
      {dialog?.kind === 'target' && model.byId.get(dialog.id) ? (
        <TargetDialog c={model.byId.get(dialog.id)!} month={month} onClose={() => setDialog(null)} />
      ) : null}
    </div>
  );
}

interface HiddenEntry {
  kind: 'group' | 'category';
  id: string;
  name: string;
  where: string;
}

/** Hidden groups (with everything in them) and individually hidden categories of visible groups. */
function hiddenEntriesOf(groups: PlanGroupDto[]): HiddenEntry[] {
  return groups.flatMap((g): HiddenEntry[] =>
    g.hidden
      ? [{ kind: 'group', id: g.id, name: g.name, where: `Group · ${g.categories.length} categories` }]
      : g.categories
          .filter((c) => c.hidden)
          .map((c) => ({ kind: 'category', id: c.id, name: c.name, where: g.name })),
  );
}

function HiddenList({ entries }: { entries: HiddenEntry[] }) {
  const { toast } = useToast();
  const unhide = async (e: HiddenEntry) => {
    try {
      await patch(`/api/${e.kind === 'group' ? 'category-groups' : 'categories'}/${e.id}`, { hidden: false });
      await queryClient.invalidateQueries({ queryKey: ['plan'] });
      toast(`${e.name} is back in your plan`);
    } catch (x) {
      toast(errorMessage(x));
    }
  };
  if (!entries.length)
    return (
      <div className="empty" style={{ padding: 40 }}>
        <p>Nothing is hidden.</p>
      </div>
    );
  return (
    <>
      {entries.map((e) => (
        <div className="hid-row" key={e.id}>
          <div>
            <div className="nm">{e.name}</div>
            <small>{e.where}</small>
          </div>
          <button className="btn" onClick={() => unhide(e)} aria-label={`Unhide ${e.name}`}>
            Unhide
          </button>
        </div>
      ))}
    </>
  );
}

type StructDialog =
  | { kind: 'group' }
  | { kind: 'category'; groupId: string }
  | { kind: 'rename-group' | 'delete-group' | 'rename-category' | 'delete-category'; id: string };

interface MenuItem {
  label: string;
  onClick: () => void;
  danger?: boolean;
}

/** Hide a category or group; it stays reachable under the Hidden chip (desktop) or Hidden button (mobile). */
function useHideItem() {
  const { toast } = useToast();
  return async (kind: 'group' | 'category', id: string, name: string) => {
    try {
      await patch(`/api/${kind === 'group' ? 'category-groups' : 'categories'}/${id}`, { hidden: true });
      await queryClient.invalidateQueries({ queryKey: ['plan'] });
      toast(`${name} hidden. Find it under Hidden`);
    } catch (e) {
      toast(errorMessage(e));
    }
  };
}

/** A "⋯" button that opens a small menu next to a row name; appears on row hover. */
function RowMenu({ label, items }: { label: string; items: MenuItem[] }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ref = useClickOutside<HTMLSpanElement>(close, open);
  return (
    <span
      className={`row-menu ${open ? 'open' : ''}`}
      ref={ref}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.key === 'Escape' && close()}
    >
      <button
        className="ib row-more"
        tabIndex={-1}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        draggable={false}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="more" size={16} />
      </button>
      {open ? (
        <div className="pop" role="menu" aria-label={label}>
          {items.map((i) => (
            <button
              key={i.label}
              role="menuitem"
              className={`pop-i ${i.danger ? 'danger' : ''}`}
              onClick={() => {
                close();
                i.onClick();
              }}
            >
              {i.label}
            </button>
          ))}
        </div>
      ) : null}
    </span>
  );
}

function ActionSheet({ title, items, onClose }: { title: string; items: MenuItem[]; onClose: () => void }) {
  return (
    <Sheet onClose={onClose} label={`${title} actions`}>
      <div className="sh-h">
        <b>{title}</b>
      </div>
      <div className="auto">
        {items.map((i) => (
          <button
            key={i.label}
            className={i.danger ? 'danger-t' : ''}
            onClick={() => {
              onClose();
              i.onClick();
            }}
          >
            {i.label}
          </button>
        ))}
      </div>
    </Sheet>
  );
}

/** Create / rename / delete dialogs for groups and categories, shared by the desktop and mobile Plan. */
function StructureDialogs({
  dialog,
  model,
  onClose,
}: {
  dialog: StructDialog | { kind: 'target'; id: string } | null;
  model: PlanModel;
  onClose: () => void;
}) {
  if (!dialog || dialog.kind === 'target') return null;
  switch (dialog.kind) {
    case 'group':
      return (
        <NameDialog
          title="New category group"
          label="Group name"
          onClose={onClose}
          onSave={(name) => post('/api/category-groups', { name })}
        />
      );
    case 'category':
      return (
        <NameDialog
          title="New category"
          label="Category name"
          onClose={onClose}
          onSave={(name) => post('/api/categories', { groupId: dialog.groupId, name })}
        />
      );
    case 'rename-group': {
      const g = model.groups.find((x) => x.id === dialog.id);
      return g ? (
        <NameDialog
          title="Rename group"
          label="Group name"
          initial={g.name}
          onClose={onClose}
          onSave={(name) => patch(`/api/category-groups/${g.id}`, { name })}
        />
      ) : null;
    }
    case 'rename-category': {
      const c = model.byId.get(dialog.id);
      return c ? (
        <NameDialog
          title="Rename category"
          label="Category name"
          initial={c.name}
          onClose={onClose}
          onSave={(name) => patch(`/api/categories/${c.id}`, { name })}
        />
      ) : null;
    }
    case 'delete-category': {
      const c = model.byId.get(dialog.id);
      return c ? <DeleteCategoryDialog c={c} model={model} onClose={onClose} /> : null;
    }
    case 'delete-group': {
      const g = model.groups.find((x) => x.id === dialog.id);
      return g ? <DeleteGroupDialog g={g} onClose={onClose} /> : null;
    }
  }
}

function DeleteCategoryDialog({
  c,
  model,
  onClose,
}: {
  c: PlanCategoryDto;
  model: PlanModel;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const usage = useQuery({
    queryKey: ['category-usage', c.id],
    queryFn: () => get<{ transactions: number; assignedMonths: number }>(`/api/categories/${c.id}/usage`),
    gcTime: 0,
  });
  const txs = usage.data?.transactions ?? 0;
  const months = usage.data?.assignedMonths ?? 0;
  const others = model.groups
    .map((g) => ({ ...g, categories: g.categories.filter((x) => x.id !== c.id && !x.hidden) }))
    .filter((g) => !g.hidden && g.categories.length);
  const [mode, setMode] = useState<'delete' | 'move'>('delete');
  const [moveTo, setMoveTo] = useState('');
  const dest = moveTo || others[0]?.categories[0]?.id || '';
  const moving = txs > 0 && mode === 'move';
  const run = async () => {
    try {
      const q = txs === 0 ? '' : moving ? `?moveTo=${dest}` : '?deleteTransactions=1';
      await del(`/api/categories/${c.id}${q}`);
      toast(txs > 0 && !moving ? `${c.name} and its ${txs} transactions deleted` : `${c.name} deleted`);
      onClose();
    } catch (e) {
      toast(errorMessage(e));
    }
  };
  return (
    <Dialog
      title={`Delete ${c.name}?`}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <span className="sp" />
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            style={{ background: 'var(--neg)' }}
            disabled={usage.isPending || (moving && !dest)}
            onClick={run}
          >
            {txs > 0 && !moving
              ? `Delete category and ${txs} transaction${txs === 1 ? '' : 's'}`
              : 'Delete category'}
          </button>
        </>
      }
    >
      {usage.isPending ? (
        <Spinner />
      ) : (
        <>
          <p>
            {months > 0
              ? `Money assigned to it in ${months} month${months === 1 ? '' : 's'} goes back to Ready to Assign. `
              : ''}
            {txs === 0 ? "This can't be undone." : `It has ${txs} transaction${txs === 1 ? '' : 's'}.`}
          </p>
          {txs > 0 ? (
            <>
              <label className="radio">
                <input type="radio" checked={mode === 'delete'} onChange={() => setMode('delete')} />
                <span>
                  <b>Delete them too</b>
                  <small>The money they spent goes back into your accounts. This can't be undone.</small>
                </span>
              </label>
              <label className="radio">
                <input
                  type="radio"
                  checked={mode === 'move'}
                  onChange={() => setMode('move')}
                  disabled={!others.length}
                />
                <span>
                  <b>Keep them in another category</b>
                  <small>Transactions and assigned money move over, so spending history stays intact.</small>
                </span>
              </label>
              {mode === 'move' ? (
                <label className="field">
                  Move them to
                  <select className="input" value={dest} onChange={(e) => setMoveTo(e.target.value)}>
                    {others.map((g) => (
                      <optgroup key={g.id} label={g.name}>
                        {g.categories.map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.name}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </label>
              ) : null}
            </>
          ) : null}
        </>
      )}
    </Dialog>
  );
}

function DeleteGroupDialog({ g, onClose }: { g: PlanGroupDto; onClose: () => void }) {
  const { toast } = useToast();
  const empty = g.categories.length === 0;
  const run = async () => {
    try {
      await del(`/api/category-groups/${g.id}`);
      await queryClient.invalidateQueries({ queryKey: ['plan'] });
      toast(`${g.name} deleted`);
      onClose();
    } catch (e) {
      toast(errorMessage(e));
    }
  };
  return (
    <Dialog
      title={empty ? `Delete ${g.name}?` : `${g.name} isn't empty`}
      onClose={onClose}
      size="sm"
      footer={
        empty ? (
          <>
            <span className="sp" />
            <button className="btn ghost" onClick={onClose}>
              Cancel
            </button>
            <button className="btn primary" style={{ background: 'var(--neg)' }} onClick={run}>
              Delete group
            </button>
          </>
        ) : (
          <>
            <span className="sp" />
            <button className="btn primary" onClick={onClose}>
              Got it
            </button>
          </>
        )
      }
    >
      {empty ? (
        <p>The group is empty. This can't be undone.</p>
      ) : (
        <>
          <p>
            Move its {g.categories.length === 1 ? 'category' : `${g.categories.length} categories`} to another
            group (drag them over) or delete them first:
          </p>
          <p>{g.categories.map((c) => c.name).join(', ')}</p>
        </>
      )}
    </Dialog>
  );
}

function FxLine({ fx, fmt }: { fx: number; fmt: Fmt }) {
  if (!fx) return <div className="fxline" />;
  return (
    <div className="fxline">
      <Icon name="swap" size={13} />
      incl. FX revaluation{' '}
      <b style={{ color: fx < 0 ? 'var(--neg)' : undefined }}>
        {fmt.num(fx, 0, 'always')} {fmt.base}
      </b>{' '}
      this month
    </div>
  );
}

interface RowProps {
  c: PlanCategoryDto;
  month: string;
  fmt: Fmt;
  selected: boolean;
  checked: boolean;
  onCheck: (v: boolean) => void;
  onSelect: () => void;
  onAction: (a: 'rename' | 'hide' | 'delete') => void;
  editing: { text: string } | null;
  onFocus: (input: HTMLInputElement) => void;
  onType: (text: string) => void;
  onBlur: () => void;
  onKey: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  pop: boolean;
  onPill: () => void;
  closePop: () => void;
  model: PlanModel;
  dragging: boolean;
  dragActive: boolean;
  onDragStart: () => void;
  onDrop: (e: DragEvent) => void;
}

function CategoryRowDesk(p: RowProps) {
  const { c, fmt, month } = p;
  const snoozed = isSnoozedNow(c.target, month);
  return (
    <div
      className={`ct-row cat ${p.selected ? 'is-sel' : ''}`}
      data-row={c.id}
      draggable
      onDragStart={(e) => {
        if ((e.target as HTMLElement).tagName === 'INPUT') return e.preventDefault();
        e.dataTransfer.effectAllowed = 'move';
        p.onDragStart();
      }}
      onDragOver={(e) => p.dragActive && e.preventDefault()}
      onDrop={p.onDrop}
      style={p.dragging ? { opacity: 0.4 } : undefined}
    >
      <span>
        <input
          type="checkbox"
          className="cb"
          tabIndex={-1}
          checked={p.checked}
          onChange={(e) => p.onCheck(e.target.checked)}
          aria-label={`Select ${c.name}`}
        />
      </span>
      <div className="cn" onClick={p.onSelect}>
        <div className="nm">
          <span className="drag" aria-hidden="true">
            <Icon name="grip" size={12} />
          </span>
          {c.name}
          {snoozed ? <span className="snz">Snoozed</span> : null}
          {c.pinned ? <Icon name="pin" size={12} className="muted" /> : null}
          <RowMenu
            label={`${c.name} actions`}
            items={[
              { label: 'Rename', onClick: () => p.onAction('rename') },
              { label: 'Hide', onClick: () => p.onAction('hide') },
              { label: 'Delete…', danger: true, onClick: () => p.onAction('delete') },
            ]}
          />
        </div>
        <div className="tg">
          <TargetBar c={c} month={month} />
          <span className={`st ${c.state}`}>{statusText(c, month, fmt)}</span>
        </div>
      </div>
      <div className="r">
        <MoneyInput
          className="as-in"
          data-as={c.id}
          value={p.editing ? p.editing.text : fmt.num(c.assigned, 2)}
          aria-label={`Assigned to ${c.name}`}
          onFocus={(e) => p.onFocus(e.currentTarget)}
          onChange={p.onType}
          onBlur={p.onBlur}
          onKeyDown={p.onKey}
        />
      </div>
      <div className="r act-c">{fmt.num(c.activity)}</div>
      <div className="pill-c">
        <CatPill c={c} onClick={p.onPill} />
        {p.pop ? <MovePopover c={c} model={p.model} fmt={fmt} onClose={p.closePop} /> : null}
      </div>
    </div>
  );
}

function MovePopover({
  c,
  model,
  fmt,
  onClose,
}: {
  c: PlanCategoryDto;
  model: PlanModel;
  fmt: Fmt;
  onClose: () => void;
}) {
  const ref = useClickOutside<HTMLDivElement>(onClose);
  const short = -c.available;
  const [amount, setAmount] = useState(fmt.num(Math.max(0, c.available), 2));
  const overspent = model.cats.filter((x) => x.id !== c.id && x.state === 'neg');
  const [to, setTo] = useState(overspent[0]?.id ?? 'rta');
  if (c.available < 0) {
    const sources = model.cats
      .filter((x) => x.id !== c.id && x.available > 0 && !x.hidden)
      .sort((a, b) => b.available - a.available)
      .slice(0, 6);
    return (
      <div className="pop" ref={ref} role="dialog" aria-label="Cover overspending">
        <div className="pop-h">
          <b>Cover overspending</b>
          <span>
            {c.name} is short{' '}
            <b className="neg-t">
              {fmt.num(short)} {fmt.base}
            </b>
            . Take it from:
          </span>
        </div>
        <div className="pop-l">
          {sources.map((x) => (
            <button
              key={x.id}
              className="pop-i"
              onClick={async () => {
                onClose();
                await model.move(x.id, c.id, Math.min(short, x.available));
              }}
            >
              <span>{x.name}</span>
              <CatPill c={x} />
            </button>
          ))}
          {!sources.length ? (
            <div className="muted" style={{ padding: '6px 8px', fontSize: 13 }}>
              No category has money available.
            </div>
          ) : null}
        </div>
        <div className="pop-f">
          <span>Ready to Assign: {fmt.num(model.rta)}</span>
          <button
            onClick={async () => {
              onClose();
              await model.move('rta', c.id, short);
            }}
          >
            Use Ready to Assign
          </button>
        </div>
      </div>
    );
  }
  const submit = async () => {
    const v = evalAssignInput(amount, 0, fmt.locale);
    if (!v || v <= 0) return;
    onClose();
    await model.move(c.id, to, v);
  };
  return (
    <div className="pop" ref={ref} role="dialog" aria-label="Move money">
      <div className="pop-h">
        <b>Move money</b>
        <span>
          From {c.name} · {fmt.num(c.available)} available
        </span>
      </div>
      <label>
        Amount
        <MoneyInput
          className="fld"
          value={amount}
          onChange={setAmount}
          autoFocus
          onKeyDown={(e) => e.key === 'Enter' && submit()}
        />
      </label>
      <label>
        To
        <select className="fld" value={to} onChange={(e) => setTo(e.target.value)}>
          <option value="rta">Ready to Assign</option>
          {model.cats
            .filter((x) => x.id !== c.id && !x.hidden)
            .map((x) => (
              <option key={x.id} value={x.id}>
                {x.name} ({fmt.num(x.available)})
              </option>
            ))}
        </select>
      </label>
      <div className="pop-btns">
        <button className="btn ghost" onClick={onClose}>
          Cancel
        </button>
        <button className="btn primary" onClick={submit}>
          Move
        </button>
      </div>
    </div>
  );
}

/** The Ready to Assign "Assign" button: a YNAB-style menu of quick auto-assign options for every category. */
function AssignMenu({
  model,
  fmt,
  onTypeAmounts,
}: {
  model: PlanModel;
  fmt: Fmt;
  onTypeAmounts: () => void;
}) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ref = useClickOutside<HTMLDivElement>(close, open);
  return (
    <div className="rta-menu" ref={ref} onKeyDown={(e) => e.key === 'Escape' && close()}>
      <button
        className="btn primary"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {model.rta < 0 ? 'Fix this' : 'Assign'} <Icon name="chevD" size={14} />
      </button>
      {open ? (
        <div className="pop" role="menu" aria-label="Auto-assign">
          <div className="pop-h">
            <b>Auto-assign</b>
            <span>Fills every category in one go. Undo brings it back.</span>
          </div>
          <div onClick={close}>
            <AutoButtons model={model} fmt={fmt} />
          </div>
          <div className="pop-f">
            <span>Prefer to type amounts?</span>
            <button
              onClick={() => {
                close();
                onTypeAmounts();
              }}
            >
              Jump to the first one
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AutoButtons({ model, ids, fmt }: { model: PlanModel; ids?: string[]; fmt: Fmt }) {
  const cats = ids
    ? ids.map((id) => model.byId.get(id)!).filter(Boolean)
    : model.cats.filter((c) => !c.hidden);
  const s = (f: (c: PlanCategoryDto) => number) => cats.reduce((a, c) => a + f(c), 0);
  const need = s((c) => c.need);
  const scope = ids && ids.length === 1 ? 'one' : 'many';
  // Overspending is covered from Ready to Assign (the server stops when it runs out)
  const short = cats.reduce((a, c) => a + Math.max(0, -c.available), 0);
  const cover = Math.min(short, Math.max(0, model.rta));
  const items: [string, string, number, boolean?][] = [
    ['underfunded', 'Underfunded', need, true],
    ['overspent', 'Cover overspending', cover, true],
    ['last_month_assigned', 'Assigned last month', s((c) => c.auto.lastAssigned)],
    ['last_month_spent', 'Spent last month', s((c) => c.auto.lastSpent)],
    ['average_assigned', 'Average assigned', s((c) => c.auto.avgAssigned)],
    ['average_spent', 'Average spent', s((c) => c.auto.avgSpent)],
    ['reset', scope === 'one' ? 'Reset assigned' : 'Reset all assigned', 0],
  ];
  return (
    <div className="auto">
      {items.map(([k, label, v, plus]) =>
        (k === 'underfunded' && scope === 'one' && !need) || (k === 'overspent' && !cover) ? null : (
          <button key={k} onClick={() => model.autoAssign(k, ids)}>
            {label} <b>{plus ? `+${fmt.num(v)}` : fmt.num(v)}</b>
          </button>
        ),
      )}
    </div>
  );
}

/**
 * Scheduled occurrences for a category until the month ends (scheduled-transactions.md §5). Display only: they
 * don't change Available until they are entered.
 */
function UpcomingThisMonth({ categoryId, month }: { categoryId: string; month: string }) {
  const { fmt } = useHousehold();
  const scheds = useScheduled().data;
  const rows = useMemo(() => {
    const out: {
      key: string;
      date: string;
      payee: string;
      account: string;
      amount: number;
      currency: 'RSD' | 'EUR';
      estimate: boolean;
    }[] = [];
    for (const s of scheds ?? []) {
      const parts = s.splits.filter((p) => p.categoryId === categoryId);
      if (s.categoryId !== categoryId && !parts.length) continue;
      const amount = s.categoryId === categoryId ? s.amount : parts.reduce((sum, p) => sum + p.amount, 0);
      const from = s.dateNext > firstDay(month) ? s.dateNext : firstDay(month);
      for (const d of occurrencesBetween(s.dateFirst, s.frequency, from, lastDay(month), s.endDate))
        out.push({
          key: `${s.id}|${d}`,
          date: d,
          payee: s.payeeName,
          account: s.accountName,
          amount,
          currency: s.currency,
          estimate: s.kind === 'loan_interest',
        });
    }
    return out.sort((a, b) => (a.date < b.date ? -1 : 1));
  }, [scheds, categoryId, month]);
  if (!rows.length) return null;
  return (
    <section>
      <div className="h4">Upcoming this month</div>
      {rows.map((r) => (
        <div className="mini-tx" key={r.key}>
          <Icon name="cal" size={16} />
          <div className="p">
            {r.payee || '—'}
            <small>
              {fmt.date(r.date)} · {r.account}
            </small>
          </div>
          <b style={{ fontWeight: 500, color: 'var(--ink-3)' }}>
            {r.estimate ? '≈ ' : ''}
            {r.currency === fmt.base ? fmt.num(r.amount, 2) : fmt.money(r.amount, r.currency, 2)}
          </b>
        </div>
      ))}
      <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        Scheduled · not in Available until entered
      </div>
    </section>
  );
}

function Inspector({
  c,
  group,
  month,
  model,
  onClose,
  onEditTarget,
  onDelete,
}: {
  c: PlanCategoryDto;
  group: PlanGroupDto;
  month: string;
  model: PlanModel;
  onClose: () => void;
  onEditTarget: () => void;
  onDelete: () => void;
}) {
  const { fmt } = useHousehold();
  const { toast } = useToast();
  const navigate = useNavigate();
  const txs = useTransactions({ categoryId: c.id, month, limit: '5' });
  const [note, setNote] = useState(c.note);
  const [renaming, setRenaming] = useState(false);
  const noteTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => setNote(c.note), [c.id, c.note]);
  const saveNote = (v: string) => {
    setNote(v);
    clearTimeout(noteTimer.current);
    noteTimer.current = setTimeout(
      () => void patch(`/api/categories/${c.id}`, { note: v }).catch((e) => toast(errorMessage(e))),
      600,
    );
  };
  const update = async (body: Record<string, unknown>) => {
    try {
      await patch(`/api/categories/${c.id}`, body);
      await queryClient.invalidateQueries({ queryKey: ['plan'] });
    } catch (e) {
      toast(errorMessage(e));
    }
  };
  const snooze = async () => {
    try {
      await post(`/api/categories/${c.id}/snooze`, { month: isSnoozedNow(c.target, month) ? null : month });
      await queryClient.invalidateQueries({ queryKey: ['plan'] });
    } catch (e) {
      toast(errorMessage(e));
    }
  };
  const prev = monthName(addMonths(month, -1));
  const { byBalance, goal, funded: fundedNow } = targetProgress(c);
  const pct = c.target ? Math.min(100, goal ? (fundedNow / goal) * 100 : 100) : 0;
  const snoozed = isSnoozedNow(c.target, month);
  return (
    <aside className="insp" aria-label={`${c.name} details`}>
      <div className="insp-h">
        <div style={{ minWidth: 0 }}>
          <small>{group.name}</small>
          {renaming ? (
            <input
              className="input"
              defaultValue={c.name}
              autoFocus
              onBlur={(e) => {
                setRenaming(false);
                if (e.target.value.trim() && e.target.value !== c.name) void update({ name: e.target.value });
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                if (e.key === 'Escape') setRenaming(false);
              }}
            />
          ) : (
            <h3 onDoubleClick={() => setRenaming(true)} title="Double-click to rename">
              {c.name}
            </h3>
          )}
        </div>
        <div style={{ display: 'flex' }}>
          <button
            className={`ib pin-b ${c.pinned ? 'on' : ''}`}
            onClick={() => update({ pinned: !c.pinned })}
            title={c.pinned ? 'Unpin from Home' : 'Pin to Home'}
            aria-pressed={c.pinned}
          >
            <Icon name="pin" />
          </button>
          <button className="ib" onClick={onClose} title="Close" aria-label="Close">
            <Icon name="x" />
          </button>
        </div>
      </div>
      <div className="insp-bal">
        <div className="top">
          <span style={{ fontWeight: 600 }}>Available</span>
          <CatPill c={c} />
        </div>
        <div className="kv">
          <span>Left over from {prev}</span>
          <b>{fmt.num(c.carryIn)}</b>
        </div>
        <div className="kv">
          <span>Assigned in {monthName(month)}</span>
          <b>{fmt.num(c.assigned)}</b>
        </div>
        <div className="kv">
          <span>Activity</span>
          <b>{fmt.num(c.activity)}</b>
        </div>
      </div>
      <section>
        <div className="h4">
          Target
          {c.target ? (
            <button
              className="btn ghost"
              style={{ padding: '2px 6px', fontSize: 12, textTransform: 'none', letterSpacing: 0 }}
              onClick={onEditTarget}
            >
              Edit
            </button>
          ) : null}
        </div>
        {c.target ? (
          <div className="tgt">
            <div className="k">{targetSentence(c.target, fmt)}</div>
            <div className="a">
              {fmt.num(goal)}{' '}
              <span style={{ font: '400 13px var(--sans)', color: 'var(--ink-3)' }}>
                {byBalance ? 'target balance' : 'needed this month'}
              </span>
            </div>
            <div className={`bar ${snoozed ? 'snz' : c.state}`}>
              <i style={{ width: `${snoozed ? 100 : Math.max(3, pct)}%` }} />
            </div>
            <div className="meta">
              <span>{byBalance ? `${fmt.num(fundedNow)} saved` : `${fmt.num(c.funded)} funded`}</span>
              <span className={`${c.state}-t`}>
                {snoozed
                  ? 'Snoozed'
                  : byBalance
                    ? fundedNow >= goal
                      ? 'Target reached'
                      : `${fmt.num(goal - fundedNow)} to go`
                    : c.need
                      ? `${fmt.num(c.need)} to go`
                      : 'Fully funded'}
              </span>
            </div>
            <div className="acts">
              <Btn icon="target" onClick={onEditTarget}>
                Edit target
              </Btn>
              <Btn variant="ghost" onClick={snooze}>
                {snoozed ? 'Unsnooze' : `Snooze for ${monthName(month, true)}`}
              </Btn>
            </div>
          </div>
        ) : (
          <div className="tgt" style={{ textAlign: 'center', color: 'var(--ink-3)' }}>
            No target yet
            <div className="acts" style={{ justifyContent: 'center' }}>
              <Btn icon="target" onClick={onEditTarget}>
                Create target
              </Btn>
            </div>
          </div>
        )}
      </section>
      <section>
        <div className="h4">Auto-assign</div>
        <AutoButtons model={model} ids={[c.id]} fmt={fmt} />
      </section>
      <section>
        <div className="h4">Notes</div>
        <textarea
          className="notes"
          placeholder="Add a note…"
          value={note}
          onChange={(e) => saveNote(e.target.value)}
          aria-label="Notes"
        />
      </section>
      <section>
        <div className="h4">
          Recent transactions
          <button
            style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500, color: 'var(--brand)' }}
            onClick={() => navigate(`/accounts/all?category=${c.id}&month=${month}`)}
          >
            View all
          </button>
        </div>
        {txs.data?.length ? (
          txs.data.map((t) => (
            <div className="mini-tx" key={t.id}>
              <Avatar id={t.createdBy} />
              <div className="p">
                {t.payeeName || '—'}
                <small>
                  {fmt.date(t.date)} · {t.accountName}
                </small>
              </div>
              <b style={{ fontWeight: 500 }}>
                {fmt.num(t.splits.find((s) => s.categoryId === c.id)?.baseAmount ?? t.baseAmount, 2)}
              </b>
            </div>
          ))
        ) : (
          <div className="muted" style={{ fontSize: 13 }}>
            No transactions this month.
          </div>
        )}
      </section>
      <UpcomingThisMonth categoryId={c.id} month={month} />
      <section className="insp-manage">
        <button className="btn ghost" onClick={() => setRenaming(true)}>
          <Icon name="edit" size={14} /> Rename
        </button>
        <button className="btn ghost" onClick={() => update({ hidden: true }).then(onClose)}>
          <Icon name="eye" size={14} /> Hide category
        </button>
        <button className="btn ghost danger" onClick={onDelete}>
          <Icon name="trash" size={14} /> Delete
        </button>
      </section>
    </aside>
  );
}

function BulkInspector({
  ids,
  model,
  fmt,
  onClear,
}: {
  ids: string[];
  model: PlanModel;
  fmt: Fmt;
  onClear: () => void;
}) {
  return (
    <aside className="insp">
      <div className="insp-h">
        <div>
          <small>Selection</small>
          <h3>{ids.length} categories</h3>
        </div>
        <button className="ib" onClick={onClear} aria-label="Clear selection">
          <Icon name="x" />
        </button>
      </div>
      <section>
        <div className="h4">Auto-assign selected</div>
        <AutoButtons model={model} ids={ids} fmt={fmt} />
      </section>
    </aside>
  );
}

function MonthSummary({ model, month }: { model: PlanModel; month: string }) {
  const { fmt } = useHousehold();
  const plan = model.plan!;
  const cats = model.cats.filter((c) => !c.hidden);
  const s = (f: (c: PlanCategoryDto) => number) => cats.reduce((a, c) => a + f(c), 0);
  const prev = monthName(addMonths(month, -1), true);
  const cur = monthName(month, true);
  return (
    <aside className="insp">
      <div className="insp-h">
        <div>
          <small>Month summary</small>
          <h3>{formatMonthLong(month)}</h3>
        </div>
      </div>
      <div className="sumgrid">
        <div>
          <small>Left over from {prev}</small>
          <b>{fmt.num(s((c) => c.carryIn))}</b>
        </div>
        <div>
          <small>Assigned in {cur}</small>
          <b>{fmt.num(s((c) => c.assigned))}</b>
        </div>
        <div>
          <small>Activity</small>
          <b>{fmt.num(s((c) => c.activity))}</b>
        </div>
        <div>
          <small>Available</small>
          <b>{fmt.num(s((c) => c.available))}</b>
        </div>
      </div>
      <section>
        <div className="h4">Auto-assign all categories</div>
        <AutoButtons model={model} fmt={fmt} />
      </section>
      <section>
        <div className="h4">Cost to be me</div>
        <div className="tgt">
          <div className="k">Sum of monthly targets</div>
          <div className="a">
            {fmt.num(plan.costToBeMe)} {fmt.base}
          </div>
          <div className="meta" style={{ marginTop: 0 }}>
            <span>Last month's income</span>
            <span>{fmt.num(plan.lastMonthIncome)}</span>
          </div>
        </div>
      </section>
      <section>
        <div className="h4">FX revaluation</div>
        <div className="kv">
          <span>EUR accounts revalued at {fmt.rate(plan.rate.rate)}</span>
          <b className={plan.fxThisMonth < 0 ? 'neg-t' : 'pos-t'}>{fmt.num(plan.fxThisMonth, 0, 'always')}</b>
        </div>
        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          This goes straight to Ready to Assign and is kept separate from every category.
        </div>
      </section>
      {plan.overspentLastMonth ? (
        <section>
          <div className="h4">Overspent last month</div>
          <div className="kv">
            <span>Taken from this month's Ready to Assign</span>
            <b className="neg-t">−{fmt.num(plan.overspentLastMonth)}</b>
          </div>
        </section>
      ) : null}
    </aside>
  );
}

export function NameDialog({
  title,
  label,
  onClose,
  onSave,
  initial = '',
}: {
  title: string;
  label: string;
  onClose: () => void;
  onSave: (name: string) => Promise<unknown>;
  initial?: string;
}) {
  const [name, setName] = useState(initial);
  const [err, setErr] = useState<string | null>(null);
  const save = async () => {
    if (!name.trim()) return;
    try {
      await onSave(name.trim());
      await queryClient.invalidateQueries({ queryKey: ['plan'] });
      onClose();
    } catch (e) {
      setErr(errorMessage(e));
    }
  };
  return (
    <Dialog
      title={title}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <span className="sp" />
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={save}>
            Save
          </button>
        </>
      }
    >
      <label className="field">
        {label}
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && save()}
          maxLength={60}
        />
      </label>
      {err ? <div className="err-t">{err}</div> : null}
    </Dialog>
  );
}

// ───────────────────────── Mobile ─────────────────────────

function PlanMobile({ model, month }: { model: PlanModel; month: string }) {
  const { fmt } = useHousehold();
  const go = useMonthNav(month);
  const plan = model.plan!;
  const [open, setOpen] = useState<string | null>(null);
  const [sheet, setSheet] = useState<string | null>(() => initialCategory());
  const [filter, setFilter] = useState<Filter>('all');
  const [filterSheet, setFilterSheet] = useState(false);
  const [hiddenSheet, setHiddenSheet] = useState(false);
  const hiddenEntries = useMemo(() => hiddenEntriesOf(model.groups), [model.groups]);
  const [dialog, setDialog] = useState<null | StructDialog>(null);
  const [menu, setMenu] = useState<null | { kind: 'group' | 'category'; id: string }>(null);
  const hideItem = useHideItem();
  const st = rtaState(model.rta);
  const firstNeeding = model.cats.find((c) => !c.hidden && (c.state === 'warn' || c.state === 'neg'));
  return (
    <>
      <div className="m-h">
        <h1>Plan</h1>
        <button
          className="ib"
          aria-label="Filter categories"
          onClick={() => setFilterSheet(true)}
          style={filter !== 'all' ? { color: 'var(--brand)' } : undefined}
        >
          <Icon name="filter" />
        </button>
      </div>
      <div className={`m-rta ${st}`} data-testid="rta">
        <div>
          <small>{model.rta < 0 ? 'Assigned too much' : 'Ready to Assign'}</small>
          <b>
            {fmt.num(model.rta)}
            <span> {fmt.base}</span>
          </b>
          {plan.fxThisMonth ? <em>incl. FX {fmt.num(plan.fxThisMonth, 0, 'always')}</em> : null}
        </div>
        {model.rta !== 0 ? (
          <button
            className="btn"
            onClick={() => firstNeeding && (setOpen(firstNeeding.id), setSheet(firstNeeding.id))}
          >
            {model.rta < 0 ? 'Fix' : 'Assign'}
          </button>
        ) : null}
      </div>
      <div className="m-month">
        <button className="ib" onClick={() => go(-1)} aria-label="Previous month">
          <Icon name="chevL" />
        </button>
        <b>{formatMonthLong(month)}</b>
        <button className="ib" onClick={() => go(1)} aria-label="Next month">
          <Icon name="chevR" />
        </button>
      </div>
      {filter !== 'all' ? (
        <div className="hint" style={{ marginTop: 4 }}>
          Showing {FILTERS.find(([f]) => f === filter)![1].toLowerCase()} ·{' '}
          <button className="link" onClick={() => setFilter('all')}>
            Show all
          </button>
        </div>
      ) : null}
      {model.groups
        .filter((g) => !g.hidden)
        .map((g) => {
          const cats = g.categories.filter((c) => !c.hidden && passFilter(c, filter, month));
          if (!cats.length && filter !== 'all') return null;
          return (
            <div className="m-grp" key={g.id}>
              <div className="m-gh">
                <span>{g.name}</span>
                <span className="m-gh-r">
                  {fmt.num(g.categories.filter((c) => !c.hidden).reduce((s, c) => s + c.available, 0))}
                  <button
                    className="ib"
                    aria-label={`Add a category to ${g.name}`}
                    onClick={() => setDialog({ kind: 'category', groupId: g.id })}
                  >
                    <Icon name="plus" size={16} />
                  </button>
                  <button
                    className="ib"
                    aria-label={`${g.name} actions`}
                    onClick={() => setMenu({ kind: 'group', id: g.id })}
                  >
                    <Icon name="more" size={16} />
                  </button>
                </span>
              </div>
              {!cats.length ? <div className="m-empty">Nothing here yet</div> : null}
              {cats.map((c) => {
                const isOpen = open === c.id;
                return (
                  <div
                    key={c.id}
                    className={`m-row ${isOpen ? 'open' : ''}`}
                    onClick={() => setOpen(isOpen ? null : c.id)}
                    role="button"
                    aria-expanded={isOpen}
                  >
                    <div className="m-r1">
                      <div>
                        <div className="nm">{c.name}</div>
                        <TargetBar c={c} month={month} />
                      </div>
                      <CatPill c={c} />
                    </div>
                    {isOpen ? (
                      <>
                        <div className="m-r2">
                          <button
                            className="m-cell"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSheet(c.id);
                            }}
                          >
                            <small>Assigned ✎</small>
                            <b>{fmt.num(c.assigned)}</b>
                          </button>
                          <div className="m-cell">
                            <small>Activity</small>
                            <b>{fmt.num(c.activity)}</b>
                          </div>
                          <div className="m-cell">
                            <small>Target</small>
                            <b>{c.target ? fmt.num(c.monthlyNeed || c.target.amount) : '—'}</b>
                          </div>
                        </div>
                        <div className={`m-st ${c.state}-t`}>{statusText(c, month, fmt) || 'No target'}</div>
                        <button
                          className="btn ghost m-manage"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMenu({ kind: 'category', id: c.id });
                          }}
                        >
                          Rename, hide or delete
                        </button>
                      </>
                    ) : null}
                  </div>
                );
              })}
            </div>
          );
        })}
      <div className="m-add">
        <Btn icon="plus" onClick={() => setDialog({ kind: 'group' })}>
          Category group
        </Btn>
        {hiddenEntries.length ? (
          <Btn variant="ghost" onClick={() => setHiddenSheet(true)}>
            Hidden ({hiddenEntries.length})
          </Btn>
        ) : null}
      </div>
      {hiddenSheet ? (
        <Sheet onClose={() => setHiddenSheet(false)} label="Hidden categories">
          <div className="sh-h">
            <b>Hidden</b>
          </div>
          <HiddenList entries={hiddenEntries} />
        </Sheet>
      ) : null}
      <StructureDialogs dialog={dialog} model={model} onClose={() => setDialog(null)} />
      {menu ? (
        <ActionSheet
          title={
            (menu.kind === 'group' ? model.groups.find((g) => g.id === menu.id) : model.byId.get(menu.id))
              ?.name ?? ''
          }
          onClose={() => setMenu(null)}
          items={
            menu.kind === 'group'
              ? [
                  {
                    label: 'Add a category',
                    onClick: () => setDialog({ kind: 'category', groupId: menu.id }),
                  },
                  { label: 'Rename group', onClick: () => setDialog({ kind: 'rename-group', id: menu.id }) },
                  {
                    label: 'Hide group',
                    onClick: () =>
                      hideItem('group', menu.id, model.groups.find((g) => g.id === menu.id)?.name ?? ''),
                  },
                  {
                    label: 'Delete group…',
                    danger: true,
                    onClick: () => setDialog({ kind: 'delete-group', id: menu.id }),
                  },
                ]
              : [
                  { label: 'Rename', onClick: () => setDialog({ kind: 'rename-category', id: menu.id }) },
                  {
                    label: 'Hide',
                    onClick: () => hideItem('category', menu.id, model.byId.get(menu.id)?.name ?? ''),
                  },
                  {
                    label: 'Delete…',
                    danger: true,
                    onClick: () => setDialog({ kind: 'delete-category', id: menu.id }),
                  },
                ]
          }
        />
      ) : null}
      {sheet && model.byId.get(sheet) ? (
        <AssignSheet c={model.byId.get(sheet)!} model={model} onClose={() => setSheet(null)} />
      ) : null}
      {filterSheet ? (
        <Sheet onClose={() => setFilterSheet(false)} label="Filter categories">
          <div className="sh-h">
            <b>Show</b>
          </div>
          <div className="qchips" style={{ flexWrap: 'wrap' }}>
            {FILTERS.map(([f, l]) => (
              <button
                key={f}
                className={`chip ${filter === f ? 'on' : ''}`}
                onClick={() => {
                  setFilter(f);
                  setFilterSheet(false);
                }}
              >
                {l}{' '}
                <span className="cnt">
                  {model.cats.filter((c) => !c.hidden && passFilter(c, f, month)).length}
                </span>
              </button>
            ))}
          </div>
        </Sheet>
      ) : null}
    </>
  );
}

/** Mobile assign sheet (screens.md §2): the value replaces Assigned; chips show resulting values. */
export function AssignSheet({
  c,
  model,
  onClose,
}: {
  c: PlanCategoryDto;
  model: PlanModel;
  onClose: () => void;
}) {
  const { fmt } = useHousehold();
  const [raw, setRaw] = useState(() => minorToRaw(c.assigned));
  const v = rawToMinor(raw);
  const after = c.carryIn + v + c.activity;
  const rtaAfter = model.rta + c.assigned - v;
  const chips: [string, number][] = [];
  if (after < 0) chips.push(['Cover overspend', -(c.carryIn + c.activity)]);
  if (c.target) chips.push(['Fund target', c.auto.underfunded]);
  chips.push(['Last month', c.auto.lastAssigned], ['Avg spent', c.auto.avgSpent]);
  const close = useCallback(onClose, [onClose]);
  return (
    <Sheet onClose={close} label={`Assign to ${c.name}`}>
      <div className="sh-h">
        <div>
          <small>Assign to</small>
          <b>{c.name}</b>
        </div>
        <div style={{ textAlign: 'right' }}>
          <small>Available after</small>
          <Pill state={after < 0 ? 'neg' : after > 0 ? 'pos' : 'zero'} value={fmt.num(after, 2)} />
        </div>
      </div>
      <div className="kp-disp">
        <span className="v">{fmt.num(v, 2)}</span>
        <span className="c">{fmt.base}</span>
      </div>
      <div className="kp-sub">
        Ready to Assign after: <b className={rtaAfter < 0 ? 'neg-t' : 'pos-t'}>{fmt.num(rtaAfter, 2)}</b>
      </div>
      <div className="qchips">
        {chips.map(([l, val]) => (
          <button key={l} className="chip" onClick={() => setRaw(minorToRaw(Math.max(0, val)))}>
            {l}: <b>{fmt.num(Math.max(0, val), 2)}</b>
          </button>
        ))}
      </div>
      <Keypad raw={raw} onChange={setRaw} locale={fmt.locale} />
      <div className="sh-act">
        <button className="btn big" onClick={onClose}>
          Cancel
        </button>
        <button
          className="btn primary big"
          onClick={async () => {
            onClose();
            await model.assignNow(c.id, v);
          }}
        >
          Assign {fmt.num(v, 2)}
        </button>
      </div>
    </Sheet>
  );
}
